import { createFrontendModule } from '@backstage/frontend-plugin-api';
import { convertLegacyRouteRef } from '@backstage/core-compat-api';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { backstagePluginSnykPlugin } from 'backstage-plugin-snyk';

import {
  isApiContentVisible,
  isCicdComponent,
  isKubernetesContentVisible,
  isSnykContentVisible,
  isSystemEntity,
} from './filters';

/**
 * Entity page content for the JLL/T developer portal, ported from the legacy
 * monolithic `EntityPage.tsx` (655 lines) to the new frontend system.
 *
 * In the legacy app every tab was an `EntityLayout.Route` guarded by an
 * `EntitySwitch`/`if` condition. In the new frontend system the catalog plugin
 * aggregates content tabs (`EntityContentBlueprint`) from every plugin/module,
 * each scoped by a `filter`. The legacy `EntitySwitch` conditions therefore
 * become per-extension `filter` predicates (see `./filters`), reusing the
 * same annotation helpers.
 *
 * The `overview` content below is the one exception: rather than adding to
 * the new frontend system's default overview aggregation (which auto-collects
 * cards from every plugin, e.g. catalog's About/Links/Has* cards, into a
 * generic 2-column layout), it *replaces* that default outright with an
 * explicit, hand-authored, per-kind layout - see `./content/OverviewContent`
 * for why and how. The remaining tabs (kubernetes/techdocs/dependencies/
 * definition/etc.) still come from the core catalog/org/api-docs/techdocs/
 * catalog-graph plugins' own aggregation; this module adds only the
 * JLL-specific and annotation-driven extras for those.
 */

/**
 * Lazy element loaders for each tab. Exported so they can be exercised
 * directly in unit tests (and so each loader is a single, traceable thunk).
 */
export const entityLoaders = {
  overview: () =>
    import('./content/OverviewContent').then(m => <m.OverviewContent />),
  cicd: () => import('./content/CicdContent').then(m => <m.CicdContent />),
  kubernetes: () =>
    import('./content/KubernetesContent').then(m => <m.KubernetesContent />),
  api: () => import('./content/ApiContent').then(m => <m.ApiContent />),
  snykContent: () =>
    import('./content/SnykContent').then(m => <m.SnykContent />),
  diagram: () =>
    import('./content/DiagramContent').then(m => <m.DiagramContent />),
};

// ---------------------------------------------------------------------------
// Content tabs
// ---------------------------------------------------------------------------

const overviewContent = EntityContentBlueprint.make({
  name: 'overview',
  params: {
    path: '/',
    title: 'Overview',
    group: 'overview',
    // No `filter`: every kind is handled (see `OverviewContent`'s own
    // per-kind branching), matching the legacy page's unconditional
    // `EntityLayout.Route path="/" title="Overview"` on every kind page.
    loader: entityLoaders.overview,
  },
});

const cicdContent = EntityContentBlueprint.make({
  name: 'cicd',
  params: {
    path: '/ci-cd',
    title: 'CI/CD',
    // Legacy: service / website / iac components.
    filter: isCicdComponent,
    loader: entityLoaders.cicd,
  },
});

const kubernetesContent = EntityContentBlueprint.make({
  name: 'kubernetes',
  params: {
    path: '/kubernetes',
    title: 'Kubernetes',
    // Legacy: service / website components with `isKubernetesAvailable`.
    filter: isKubernetesContentVisible,
    loader: entityLoaders.kubernetes,
  },
});

const apiContent = EntityContentBlueprint.make({
  name: 'api',
  params: {
    path: '/api',
    title: 'API',
    // Legacy: service components without the apidocs-dont-display suppressor.
    filter: isApiContentVisible,
    loader: entityLoaders.api,
  },
});

const snykContent = EntityContentBlueprint.make({
  name: 'snyk',
  params: {
    path: '/snyk',
    title: 'Snyk',
    // Legacy: service / library / iac components, only when Snyk is visible.
    filter: isSnykContentVisible,
    loader: entityLoaders.snykContent,
    // `SnykContent` renders the legacy routable `EntitySnykContent`; binding its
    // mount-point routeRef as this tab's routeRef mounts the route in the app
    // tree so the routable extension resolves (see `content/SnykContent.tsx`).
    routeRef: convertLegacyRouteRef(
      backstagePluginSnykPlugin.routes.entityContent,
    ),
  },
});

// The Artifactory entity-content tab is now provided natively by
// `@jllt/plugin-artifactory-frontend/alpha` (added directly in `App.tsx`). Its
// default filter matches the portal (service components, unless
// `artifactory-dont-display`, plus dockerimage resources with an
// `artifactory-id`), so no app-side content extension is registered here.

const diagramContent = EntityContentBlueprint.make({
  name: 'diagram',
  params: {
    path: '/diagram',
    title: 'Diagram',
    // Legacy: System entities.
    filter: isSystemEntity,
    loader: entityLoaders.diagram,
  },
});

/**
 * Catalog module wiring the JLL/T entity page content. Targets
 * `pluginId: 'catalog'` so the extensions are aggregated into the catalog
 * plugin's entity page alongside its built-in content - and so that
 * `overviewContent` (same kind + pluginId + name as the catalog plugin's own
 * default `overview` content) *replaces* that default instead of duplicating
 * it (see `resolveAppNodeSpecs` in `@backstage/frontend-app-api`: a module
 * extension with an id matching an existing plugin extension's id overrides
 * it in place).
 */
export const entityPageModule = createFrontendModule({
  pluginId: 'catalog',
  extensions: [
    overviewContent,
    cicdContent,
    kubernetesContent,
    apiContent,
    snykContent,
    diagramContent,
  ],
});

